import Modal from "@mui/material/Modal";
import { useRouter } from "next/router";
import * as React from "react";

import { useAuthContext } from "~auth/contexts/auth";
import { V4ReleaseModal } from "~mangomap-ui/components/v4-release-modal";
import {
	sendV4ReleaseMessageClosedEvent,
	sendV4ReleaseMessageLinkFollowedEvent,
	sendV4ReleaseMessageSeenEvent,
} from "~tag-manager/send-event";
import {
	useClaimV4ReleaseMessage,
	useRecordV4ReleaseMessageEvent,
} from "../queries/use-v4-release-message";

export const V4ReleaseMessageController = () => {
	const router = useRouter();
	const { authUser, isAuthenticated, isLoading } = useAuthContext();
	const { mutate: claimReleaseMessage } = useClaimV4ReleaseMessage();
	const { mutate: recordReleaseMessageEvent } =
		useRecordV4ReleaseMessageEvent();
	const [isOpen, setIsOpen] = React.useState(false);
	const claimedUserIdRef = React.useRef<string>();
	const isAccountRoute =
		router.pathname.startsWith("/[account-name]") ||
		typeof router.query["account-name"] === "string";

	React.useEffect(() => {
		const userId = authUser?.id;
		if (
			isLoading ||
			!isAuthenticated ||
			!isAccountRoute ||
			!userId ||
			claimedUserIdRef.current === userId
		) {
			return;
		}

		claimedUserIdRef.current = userId;
		claimReleaseMessage(undefined, {
			onError: () => {
				if (claimedUserIdRef.current === userId) {
					claimedUserIdRef.current = undefined;
				}
			},
			onSuccess: ({ shouldShow }) => {
				if (!shouldShow) return;
				setIsOpen(true);
				sendV4ReleaseMessageSeenEvent();
			},
		});
	}, [
		authUser?.id,
		claimReleaseMessage,
		isAccountRoute,
		isAuthenticated,
		isLoading,
	]);

	const handleClose = () => {
		setIsOpen(false);
		recordReleaseMessageEvent({ event: "CLOSED" });
		sendV4ReleaseMessageClosedEvent();
	};

	const handleLinkClick = () => {
		recordReleaseMessageEvent({ event: "LINK_FOLLOWED" });
		sendV4ReleaseMessageLinkFollowedEvent();
	};

	return (
		<Modal
			aria-label="MangoMap v4 release message"
			onClose={handleClose}
			open={isOpen}
			sx={{
				alignItems: "center",
				display: "flex",
				justifyContent: "center",
				p: 3,
			}}>
			<V4ReleaseModal
				description="MangoMap v4 brings a faster, more reliable mapping experience with the familiar tools you need to publish and manage your maps."
				headline="MangoMap v4 is here"
				linkHref="/mangomap-v4"
				linkLabel="See what's new"
				onClose={handleClose}
				onLinkClick={handleLinkClick}
			/>
		</Modal>
	);
};
